import React from 'react'
import PropTypes from 'prop-types'
import { withTranslation } from 'react-i18next'
import { CircularProgressbar, buildStyles } from 'react-circular-progressbar'
import { HoverStateProvider } from 'components'
import Tooltip from 'components/Tooltip'
import { AIFilled, ExplicitFilled } from '@dsny/dsny-component-library/icons'
import { CheckIcon, ExclamationIcon, placeholderRoundedCoverArt } from 'images'
import { DownloadTaskType, DownloadTaskStatus } from 'constants/enums'

class DownloadMenuCell extends React.Component {
  static propTypes = {
    t: PropTypes.func,
    download: PropTypes.object,
    downloadID: PropTypes.string,
    coverart: PropTypes.string,
    artist: PropTypes.string,
    title: PropTypes.string,
    version: PropTypes.string,
    isAIGenerated: PropTypes.bool,
    explicit: PropTypes.bool,
    status: PropTypes.string,
    type: PropTypes.string,
    trackcount: PropTypes.number,
    percentCompleted: PropTypes.number,
    onRestartDownload: PropTypes.func,
    onRemoveDownload: PropTypes.func
  }

  renderStatus = (status, percentCompleted) => {
    const { download, onRestartDownload, t } = this.props
    if (status === DownloadTaskStatus.loading) {
      return (
        <div style={{ width: '18px', height: '18px' }}>
          <CircularProgressbar
            value={percentCompleted}
            styles={buildStyles({
              rotation: 0.25, // Start the progress at the top
              strokeLinecap: 'butt', // Smooth edges for the ends of the progress path
              trailColor: '#d6d6d6', // Background circle color (gray)
              pathColor: '#3e98c7' // Progress path color (blue)
            })}
            text={''} />
        </div>
      )
    } else if (status === DownloadTaskStatus.failed) {
      return (
        <Tooltip
          hasArrow={false}
          placement="top"
          containerStyles={{
            whiteSpace: 'nowrap',
            textAlign: 'center',
            transform: 'translate(-90%, -42px)'
          }}
          text={t('download-manager.download-failed-tooltip')}>
          <button
            type="button"
            className="focus:outline-none"
            onClick={() => onRestartDownload(download)}>
            <ExclamationIcon
              viewBox="0 0 24 24"
              width={14}
              height={14}
              className="overflow-visible fill-current text-red-500" />
          </button>
        </Tooltip>
      )
    } else if (status === DownloadTaskStatus.finished) {
      return <CheckIcon className="overflow-visible" />
    } else if (status === DownloadTaskStatus.waiting) {
      return (
        <div
          className="rotate rounded-full border-2 border-gray-550"
          style={{
            width: '18px',
            height: '18px',
            borderTopColor: '#007dcc'
          }} />
      )
    }
    return null
  }

  renderWaitingMessage = (status) => {
    if (status === DownloadTaskStatus.waiting) {
      return <div className="w-12" />
    } else {
      return <div className="w-12" />
    }
  }

  renderContent = () => {
    const {
      download,
      coverart,
      artist,
      title,
      version,
      isAIGenerated,
      explicit,
      status,
      type,
      trackcount,
      percentCompleted,
      onRemoveDownload,
      t
    } = this.props
    return (
      <div className="w-full px-6 py-3 flex justify-between items-center text-gray-225 transition hover:bg-gray-800">
        <div className="flex items-center flex-grow min-w-0">
          <img
            src={coverart || placeholderRoundedCoverArt}
            alt={title}
            className="w-10 h-10 mr-2 rounded shadow-dark object-cover" />
          <div className="flex-grow min-w-0">
            <div className="flex items-center space-x-1 min-w-0">
              <p className="truncate" style={{ fontSize: '14px' }}>
                {title}
              </p>
              {version && (
                <p
                  className="flex-shrink-0"
                  style={{ fontSize: '14px' }}>
                  {` (${version})`}
                </p>
              )}
            </div>
            <div
              className="flex items-center min-w-0 text-gray-225"
              style={{ columnGap: '4px', fontSize: '12px' }}>
              {explicit && (
                <ExplicitFilled width={16} className="flex-shrink-0" />
              )}
              {isAIGenerated && (
                <AIFilled
                  width={18}
                  className="flex-shrink-0"
                  color="currentColor" />
              )}
              <span className="truncate">{artist}</span>
              {(type === DownloadTaskType.release ||
                type === DownloadTaskType.playlist) && (
                <span className="flex-shrink-0 text-cerulean">{` (${trackcount})`}</span>
              )}
            </div>
          </div>
        </div>
        <div className="w-32 flex justify-between items-center">
          {this.renderWaitingMessage(status)}
          <div className="w-6 flex items-center justify-center">
            {this.renderStatus(status, percentCompleted)}
          </div>
          <button
            type="button"
            className="py-2 pl-2 pr-0 focus:outline-none text-sm text-gray-225 transition hover:text-white"
            onClick={() => onRemoveDownload(download)}>
            {t('download-manager.clear')}
          </button>
        </div>
      </div>
    )
  }

  render() {
    const { downloadID } = this.props
    return <HoverStateProvider key={downloadID} render={this.renderContent} />
  }
}

export default withTranslation()(DownloadMenuCell)
